import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../lib/api";
import { cn } from "../lib/utils";

interface MunicipalityFilterProps {
  onFilterChange?: (sourceId: string | number | null) => void;
  className?: string;
}

export default function MunicipalityFilter({ onFilterChange, className }: MunicipalityFilterProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const municipalityParam = searchParams.get('municipality');
  // Store as string to handle both numeric IDs (municipalities) and string IDs (provincial sources)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(
    municipalityParam || null
  );

  // Fetch ALL sources from the database (municipal, regional, provincial)
  // Since this is for Brightstone only, show all available sources regardless of subscription
  const { data: municipalSources, isLoading: isLoadingMunicipal } = useQuery({
    queryKey: ['public-municipalities', 'municipal'],
    queryFn: () => apiClient.public.getMunicipalities('municipal'),
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  const { data: regionalSources, isLoading: isLoadingRegional } = useQuery({
    queryKey: ['public-municipalities', 'regional'],
    queryFn: () => apiClient.public.getMunicipalities('regional'),
    staleTime: 10 * 60 * 1000,
  });

  const { data: provincialSources, isLoading: isLoadingProvincial } = useQuery({
    queryKey: ['public-municipalities', 'provincial'],
    queryFn: () => apiClient.public.getMunicipalities('provincial'),
    staleTime: 10 * 60 * 1000,
  });

  // Combine all sources from all types
  const allSources = React.useMemo(() => {
    const sources: Array<{ id: string | number; name: string; municipality_id?: number; source_type: string }> = [];
    
    // Add municipal sources
    if (municipalSources && Array.isArray(municipalSources)) {
      municipalSources.forEach((source: any) => {
        sources.push({
          id: source.id,
          name: source.name,
          municipality_id: source.id, // For municipal sources, id is the municipality_id
          source_type: 'municipal'
        });
      });
    }
    
    // Add regional sources
    if (regionalSources && Array.isArray(regionalSources)) {
      regionalSources.forEach((source: any) => {
        sources.push({
          id: source.id,
          name: source.name,
          municipality_id: source.id, // For regional sources, id is the municipality_id
          source_type: 'regional'
        });
      });
    }
    
    // Add provincial sources
    // Provincial sources in the dashboard use string IDs based on name (e.g., "ero", "orr")
    // The API creates IDs using: source_name.lower().replace(' ', '_') which replaces ALL spaces
    // We need to create the same ID format for matching
    if (provincialSources && Array.isArray(provincialSources)) {
      provincialSources.forEach((source: any) => {
        // Create string ID from name (matching dashboard format exactly)
        // Python's .replace(' ', '_') replaces ALL spaces with underscores
        // JavaScript equivalent: .replace(/ /g, '_') replaces all spaces
        // Example: "Ministry of Finance" -> "ministry_of_finance"
        const stringId = source.name?.toLowerCase().replace(/ /g, '_') || source.id?.toString();
        sources.push({
          id: stringId, // Use string ID for provincial sources to match dashboard format
          name: source.name,
          municipality_id: source.id, // Keep numeric ID for reference
          source_type: 'provincial'
        });
      });
    }
    
    return sources;
  }, [municipalSources, regionalSources, provincialSources]);

  const isLoading = isLoadingMunicipal || isLoadingRegional || isLoadingProvincial;

  // Update URL when filter changes
  useEffect(() => {
    if (selectedSourceId) {
      searchParams.set('municipality', selectedSourceId);
    } else {
      searchParams.delete('municipality');
    }
    setSearchParams(searchParams, { replace: true });
    
    // Notify parent component - convert to number if it's numeric, otherwise keep as string
    if (onFilterChange) {
      const numericId = selectedSourceId && !isNaN(Number(selectedSourceId)) 
        ? Number(selectedSourceId) 
        : selectedSourceId;
      onFilterChange(numericId);
    }
  }, [selectedSourceId, searchParams, setSearchParams, onFilterChange]);

  // Sync with URL param changes
  useEffect(() => {
    const paramValue = searchParams.get('municipality');
    if (paramValue && paramValue !== selectedSourceId) {
      setSelectedSourceId(paramValue);
    } else if (!paramValue && selectedSourceId !== null) {
      setSelectedSourceId(null);
    }
  }, [searchParams]);

  const handleClear = () => {
    setSelectedSourceId(null);
  };

  if (isLoading) {
    return null;
  }

  // Find selected source name
  const selectedSource = selectedSourceId
    ? allSources.find((m: any) => {
        const sourceId = (m.municipality_id?.toString() || m.id?.toString() || '').toString();
        return sourceId === selectedSourceId || m.id?.toString() === selectedSourceId;
      })
    : null;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <MapPin className="h-4 w-4 text-neutral-400 shrink-0" />
      <Select
        value={selectedSourceId || "all"}
        onValueChange={(value) => {
          if (value === "all") {
            setSelectedSourceId(null);
          } else {
            setSelectedSourceId(value);
          }
        }}
      >
        <SelectTrigger className="w-[200px] h-8 text-sm bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800">
          <SelectValue placeholder="All Sources">
            {selectedSourceId ? (
              selectedSource?.name || selectedSource?.municipality_name || `Source ${selectedSourceId}`
            ) : (
              "All Sources"
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Sources</SelectItem>
          {allSources.map((source: any) => {
            const id = (source.municipality_id?.toString() || source.id?.toString() || '').toString();
            const name = source.name || source.municipality_name || `Source ${id}`;
            return (
              <SelectItem key={`${source.source_type || 'municipal'}-${id}`} value={id}>
                {name}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {selectedSourceId && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          onClick={handleClear}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
